import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import apiClient from '../utils/api';
import { formatRelativeTime } from '../utils/dateUtils';

interface NotificationMetadata {
  seasonId?: string;
  matchGroupId?: string;
  matchGroupName?: string;
  matchWeek?: number | null;
  seasonName?: string;
  totalScored?: number;
  correctCount?: number;
  exactScoreCount?: number;
  totalPoints?: number;
  isComplete?: boolean;
}

interface Notification {
  id: string;
  type: 'scored_predictions';
  title: string;
  message: string;
  metadata: NotificationMetadata | null;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const POLL_INTERVAL_MS = 600_000;
const PULSE_DURATION_MS = 5_000;

const NotificationsBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const initialFetchDoneRef = useRef(false);
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiClient.get('/notifications');
      setNotifications(response.data.notifications);
      const count = response.data.unreadCount;
      setUnreadCount(count);

      if (!initialFetchDoneRef.current) {
        initialFetchDoneRef.current = true;
        if (count > 0) {
          setIsPulsing(true);
          pulseTimeoutRef.current = setTimeout(() => {
            setIsPulsing(false);
          }, PULSE_DURATION_MS);
        }
      }
    } catch {
      // Silently fail — bell is non-critical
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchNotifications().finally(() => setLoading(false));
    }
  }, [open, fetchNotifications]);

  const markAsRead = async (id: string) => {
    try {
      await apiClient.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.patch('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.readAt) {
      markAsRead(notification.id);
    }
    setOpen(false);
  };

  const close = () => setOpen(false);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`relative p-2 rounded-lg border transition-colors ${
          isPulsing ? 'animate-notification-bell-pulse' : ''
        }`}
        style={{
          backgroundColor: 'var(--surface)',
          borderColor: 'var(--border)',
          color: open ? 'var(--highlight)' : 'var(--text-primary)',
        }}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <BellIcon className="w-6 h-6" />
        {unreadCount > 0 && (
          <span
            className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 border-2"
            style={{ borderColor: 'var(--surface)' }}
            aria-hidden="true"
          />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} aria-hidden="true" />
          <div
            className="absolute right-0 z-50 mt-2 w-80 sm:w-96 rounded-lg border shadow-lg overflow-hidden"
            style={{
              backgroundColor: 'var(--surface)',
              borderColor: 'var(--border)',
            }}
          >
            <div
              className="flex items-center justify-between px-4 py-3 border-b border-light-border dark:border-dark-border"
            >
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Notifications
              </h3>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="text-xs font-medium hover:underline"
                  style={{ color: 'var(--highlight)' }}
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading && notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No notifications yet
                </div>
              ) : (
                <ul>
                  {notifications.map((notification) => {
                    const isUnread = !notification.readAt;
                    const meta = notification.metadata;
                    const linkTo = meta?.seasonId
                      ? `/my-predictions?season=${meta.seasonId}${meta.matchGroupId ? `&matchGroup=${meta.matchGroupId}` : ''}`
                      : '/my-predictions';

                    return (
                      <li key={notification.id}>
                        <Link
                          to={linkTo}
                          onClick={() => handleNotificationClick(notification)}
                          className={`block px-4 py-3 transition-colors hover:bg-black/5 dark:hover:bg-white/5 border-b border-light-border dark:border-dark-border last:border-b-0 ${
                            isUnread ? 'bg-highlight/5' : ''
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {isUnread && (
                              <span
                                className="mt-1.5 w-2 h-2 shrink-0 rounded-full bg-red-500"
                                aria-hidden="true"
                              />
                            )}
                            <div className={isUnread ? '' : 'ml-4'}>
                              <div
                                className="text-sm font-medium leading-snug"
                                style={{ color: 'var(--text-primary)' }}
                              >
                                {notification.title}
                              </div>
                              <div
                                className="text-sm mt-0.5 leading-snug"
                                style={{ color: 'var(--text-secondary)' }}
                              >
                                {notification.message}
                              </div>
                              <div
                                className="text-xs mt-1"
                                style={{ color: 'var(--text-secondary)' }}
                              >
                                {formatRelativeTime(notification.updatedAt)}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsBell;
